/* eslint-disable jsx-a11y/media-has-caption */
import {
  Box, SxProps, Theme, Typography
} from '@mui/material';
import {
  BrowserMultiFormatReader,
  ChecksumException,
  FormatException,
  NotFoundException
} from '@zxing/library';
import React, { useCallback, useEffect, useState } from 'react';
import { ETOSelectField } from '../Forms';
import QRScanner from './QRScanner';

const root: SxProps = {
  width: '100%',
};

const videoWrapper: SxProps<Theme> = (theme: Theme) => ({
  border: `1px solid ${theme.palette.grey[400]}`,
  borderRadius: theme.spacing(0.5),
  position: 'relative',
  height: '100%',
});

const videoDisplay: SxProps<Theme> = (theme: Theme) => ({
  boxSizing: 'border-box',
  objectFit: 'cover',
  display: 'block',
  width: '100%',
  maxHeight: theme.spacing(50),
  position: 'absolute',
});

const mediaDeviceSelection: SxProps<Theme> = (theme: Theme) => ({
  position: 'absolute',
  width: 'auto',
  top: theme.spacing(1),
  left: theme.spacing(1),
  right: theme.spacing(1),
});

const messageStyle: SxProps<Theme> = () => ({
  position: 'absolute',
  bottom: '-1px',
  left: '-1px',
  right: '-1px',
  textAlign: 'center',
  background: 'rgba(255,255,255, 0.5)',
});

export interface BarcodeReaderProps {
  enabled: boolean;
  // eslint-disable-next-line no-unused-vars
  onRead: (data: string) => void;
  customSx?: SxProps<Theme>;
  disableOverlay?: boolean;
}

const BarcodeReader: React.FC<BarcodeReaderProps> = (props) => {
  const {
    enabled, onRead, customSx, disableOverlay,
  } = props;
  const [selectedDeviceId, setSelectedDeviceId] = useState('');
  const [mediaDevices, setMediaDevices] = useState<MediaDeviceInfo[]>([]);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const codeReader = new BrowserMultiFormatReader();

  useEffect(() => {
    codeReader
      .listVideoInputDevices()
      .then((videoInputDevices) => {
        setSelectedDeviceId(videoInputDevices[0].label);
        setMediaDevices(videoInputDevices);
      })

      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error(err);
        setError(err);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const decodeContinuously = useCallback(
    () => codeReader.decodeFromVideoDevice(
      selectedDeviceId,
      'barcode-video',
      (result, err) => {
        if (result) {
          // properly decoded qr code
          onRead(result.getText());
          setMessage(result.getText());
          setError('');
        }

        if (err) {
          // As long as this error belongs into one of the following categories
          // the code reader is going to continue as excepted. Any other error
          // will stop the decoding loop.
          //
          // Excepted Exceptions:
          //
          //  - NotFoundException
          //  - ChecksumException
          //  - FormatException
          // console.log(err, 'err');
          if (err instanceof NotFoundException) {
            setError('No QR code found.');
          }

          if (err instanceof ChecksumException) {
            setError("A code was found, but it's read value was not valid.");
          }

          if (err instanceof FormatException) {
            setError('A code was found, but it was in a invalid format.');
          }
        }
      },
    ),
    // don't include dependencies on this - Bug #72013
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedDeviceId],
  );

  useEffect(() => {
    // eslint-disable-next-line no-unused-expressions
    enabled ? decodeContinuously() : codeReader.stopContinuousDecode();
  }, [decodeContinuously, enabled, selectedDeviceId]);

  return (
    /* eslint-disable react/jsx-no-useless-fragment */
    (
      <>
        {enabled && (
        // eslint-disable-next-line @typescript-eslint/ban-types
          (
          <Box sx={[root, ...(Array.isArray(customSx) ? customSx : [customSx])]}>
            <Box sx={videoWrapper}>
              <Box id="barcode-video" sx={videoDisplay} component="video" />
              {mediaDevices.length > 0 && (
              <>
                <ETOSelectField
                  name="barcode-video"
                  size="small"
                  customSx={mediaDeviceSelection}
                  items={mediaDevices}
                  itemNameSelector={(a) => a.label}
                  itemValueSelector={(a) => a.label}
                  value={selectedDeviceId}
                  handleChange={(c) => {
                    setSelectedDeviceId(
                      mediaDevices.find((b) => b.label === c.target.value)
                        ?.deviceId ?? '',
                    );
                  }}
                />
              </>
              )}
              {mediaDevices.length === 0 ?? (
              <Typography sx={{ color: 'error.main' }}>
                {' '}
                No Cameras Found
              </Typography>
              )}
              {!disableOverlay && <QRScanner />}
              <Box sx={messageStyle}>
                {error !== '' ? (
                  <Typography sx={{ color: 'error.main' }}>{error}</Typography>
                ) : (
                  <Typography sx={{ color: 'text.primary' }}>
                    {message}
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
          )
        )}
      </>
    )
  );
};

export default BarcodeReader;
